export type VisitUrgency = 'imminent' | 'today' | 'upcoming' | 'past';

export interface RelativeVisitTime {
  label: string;
  urgency: VisitUrgency;
}

export interface RelativeTimeLabels {
  yesterday: string;
  now: string;
  tomorrow: string;
  minutesAgo: (m: number) => string;
  hoursAgo: (h: number) => string;
  daysAgo: (d: number) => string;
  inMinutes: (m: number) => string;
  inHours: (h: number) => string;
  inDays: (d: number) => string;
}

/**
 * Computes a human-readable relative time label and urgency level for a visit
 * datetime (ISO string in BRT).
 *
 * @param datetime - ISO datetime string (e.g. "2026-08-16T09:45:00")
 * @param now - Optional override for current time (testing)
 * @param labels - i18n labels for time descriptions
 * @returns Label and urgency level
 */
export function getRelativeVisitTime(
  datetime: string,
  now: Date | undefined,
  labels: RelativeTimeLabels,
): RelativeVisitTime {
  const current = now ?? new Date();
  const target = new Date(datetime);
  const diffMs = target.getTime() - current.getTime();
  const diffMinutes = Math.round(diffMs / 60_000);

  if (diffMinutes < 0) {
    return buildPastLabel(Math.abs(diffMinutes), labels);
  }
  return buildFutureLabel(diffMinutes, labels);
}

/**
 * Formats a past-relative label from elapsed minutes using provided labels.
 */
function buildPastLabel(minutes: number, labels: RelativeTimeLabels): RelativeVisitTime {
  if (minutes < 60) {
    return { label: labels.minutesAgo(minutes), urgency: 'past' };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { label: labels.hoursAgo(hours), urgency: 'past' };
  }
  const days = Math.floor(hours / 24);
  if (days === 1) {
    return { label: labels.yesterday, urgency: 'past' };
  }
  return { label: labels.daysAgo(days), urgency: 'past' };
}

/**
 * Formats a future-relative label from remaining minutes using provided labels.
 */
function buildFutureLabel(minutes: number, labels: RelativeTimeLabels): RelativeVisitTime {
  if (minutes <= 120) {
    return {
      label: minutes <= 1 ? labels.now : labels.inMinutes(minutes),
      urgency: 'imminent',
    };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { label: labels.inHours(hours), urgency: 'today' };
  }
  const days = Math.floor(hours / 24);
  if (days === 1) {
    return { label: labels.tomorrow, urgency: 'upcoming' };
  }
  return { label: labels.inDays(days), urgency: 'upcoming' };
}
